import {Box, Typography} from '@mui/material';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import {useGetDocDetailsMetadataQuery} from '../../../../../hooks/document/useDocument';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {IGetDocDetailsMetadataGridDataReq} from '../../../../../api/types/document.type';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {GridRenderCellParams} from '@mui/x-data-grid';
import {t} from 'i18next';

const ROW_HEIGHT = 36;
const ROW_BUFFER_PX = 1500;
const HEADER_MIN_WIDTH = 200;
const DEFAULT_PAGE_SIZE_OPTIONS = 100;
const DEFAULT_PAGE_SIZE = 1;
const DEFAULT_PAGE_NUM = 0;

export const NoRowsOverlayWithCustomLabel = ({
  noDataLabel,
}: {
  noDataLabel: string;
}) => (
  <Box className="no-grid-overlay">
    <Typography
      className="typo-text body3_size"
      data-testid="no-meta-result"
      tabIndex={0}
    >
      {noDataLabel ?? t('docExplorer.docDataGrid.noRowsOverlayCustomText')}
    </Typography>
  </Box>
);

export const NoRowsOverlay = () => (
  <Box className="no-grid-overlay">
    <Typography
      className="typo-text body3_size"
      data-testid="no-meta-result"
      tabIndex={0}
    >
      {t('docExplorer.docDataGrid.noRowsOverlayCustomText')}
    </Typography>
  </Box>
);

export const Metadata = () => {
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const {isAssignmentContext, assignmentId} = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );
  const documentId = activeDocumentObj?.documentId;
  const preparePayload = (): IGetDocDetailsMetadataGridDataReq => {
    const payload: IGetDocDetailsMetadataGridDataReq = {
      documentId: Number(documentId),
      isAllMetaData: true,
      sourceTypeCode: '',
      assignmentSource: '',
      sourceID: 0,
      displayLength: -1,
      displayStart: 0,
      sortingColumn: 'MetaDataName,MetaDataValue',
      direction: 'ASC',
    };

    if (isAssignmentContext && assignmentId) {
      payload.isAllMetaData = false;
      payload.sourceTypeCode = 'ASSIGNMENT';
      payload.assignmentSource = 'MANAGEASSIGNMENT';
      payload.sourceID = assignmentId;
    }

    return payload;
  };

  const {data: docGridData = [], isLoading} = useGetDocDetailsMetadataQuery(
    preparePayload(),
    Boolean(documentId)
  );
  const columns = [
    {
      field: 'metaDataName',
      headerName: 'Field Name',
      flex: 1,
      minWidth: HEADER_MIN_WIDTH,
      renderCell: (param: GridRenderCellParams) => {
        return (
          <CellWrapper
            toolTipContent={param.value}
            dataTestId={`cell-wrapper-tooltip-content-metadataName-${param.id}`}
            cellParams={param}
          >
            <Typography
              className=" cell-content typo-text body4_size"
              data-testid={`meta-data-name-value-${param.id}`}
            >
              {param.value}
            </Typography>
          </CellWrapper>
        );
      },
    },
    {
      field: 'metaDataValue',
      headerName: 'Field Value',
      headerClassName: 'last-column',
      flex: 1,
      minWidth: HEADER_MIN_WIDTH,
      renderCell: (param: GridRenderCellParams) => {
        return (
          <CellWrapper
            toolTipContent={param.value}
            dataTestId={`cell-wrapper-tooltip-content-metadataValue-${param.id}`}
            cellParams={param}
          >
            <Typography
              className=" cell-content typo-text body4_size"
              data-testid={`meta-data-value-${param.id}`}
            >
              {param.value}
            </Typography>
          </CellWrapper>
        );
      },
    },
  ];

  return (
    <div className="meta-data-container" data-testid="meta-data-grid">
      {isLoading ? (
        <ArrLoadingSpinner />
      ) : (
        <ArrDataGridPro
          rows={docGridData}
          columns={columns}
          hideFooter
          disableColumnMenu
          rowHeight={ROW_HEIGHT}
          rowBufferPx={ROW_BUFFER_PX}
          getRowId={(row) => row['metaDataName']}
          className="metadata-grid"
          // Disable pagination by setting a huge pageSize (optional)
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: docGridData?.length || DEFAULT_PAGE_SIZE,
                page: DEFAULT_PAGE_NUM,
              },
            },
          }}
          noRowsOverlay={NoRowsOverlay}
          disableColumnSorting
          pageSizeOptions={[docGridData?.length || DEFAULT_PAGE_SIZE_OPTIONS]} // prevent page size dropdown
          data-testid="meta-data-grid-pro"
          entityType="metadata"
        />
      )}
    </div>
  );
};

export default Metadata;
